import React, { useState } from 'react';
import { Modal, Upload, Button, Spin, Typography } from 'antd';
import { InboxOutlined, FilePdfOutlined } from '@ant-design/icons';
import { t } from '../../i18n/i18n';
import { useJobDescription } from './hooks/useJobDescription';

const { Dragger } = Upload;
const { Text, Paragraph } = Typography;

const JobDescriptionModal = ({ visible, onClose, onSendToAssistant }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileList, setFileList] = useState([]);
  const { processingPDF, extractedText, extractTextFromPDF, validatePDFFile, resetJobDescription } =
    useJobDescription();

  const handleFileChange = async (info) => {
    const { file, fileList: newFileList } = info;

    setFileList(newFileList.slice(-1));

    if (file.status === 'removed') {
      setSelectedFile(null);
      resetJobDescription();
      return;
    }

    const originalFile = file.originFileObj || file;
    if (validatePDFFile(originalFile)) {
      setSelectedFile(originalFile);
      await extractTextFromPDF(originalFile);
    } else {
      setFileList([]);
    }
  };

  const handleSendToAssistant = () => {
    if (extractedText && onSendToAssistant) {
      onSendToAssistant(extractedText);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setFileList([]);
    resetJobDescription();
    onClose();
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    beforeUpload: (file) => {
      return false;
    },
    onChange: handleFileChange,
    onRemove: () => {
      setSelectedFile(null);
      resetJobDescription();
      return true;
    },
    accept: '.pdf',
  };

  return (
    <Modal
      title={t('CHAT_JOB_DESCRIPTION_MODAL_TITLE')}
      open={visible}
      onCancel={handleClose}
      width={700}
      footer={[
        <Button 
          key="cancel" 
          onClick={handleClose}
          className="rounded"
          style={{ borderRadius: '6px' }}
        >
          {t('CHAT_JOB_DESCRIPTION_MODAL_CANCEL')}
        </Button>,
        <Button
          key="send"
          type="primary"
          onClick={handleSendToAssistant}
          disabled={!extractedText || processingPDF}
          className="rounded"
          style={{ borderRadius: '6px' }}
        >
          {t('CHAT_JOB_DESCRIPTION_MODAL_SEND')}
        </Button>,
      ]}
    >
      <div className="mb-3">
        <Dragger {...uploadProps} className="mb-3">
          <p className="ant-upload-drag-icon">
            <FilePdfOutlined className="text-primary" style={{ fontSize: '3rem' }} />
          </p>
          <p className="ant-upload-text">{t('CHAT_JOB_DESCRIPTION_DRAG_TEXT')}</p>
          <p className="ant-upload-hint">{t('CHAT_JOB_DESCRIPTION_DRAG_HINT')}</p>
        </Dragger>

        {processingPDF && (
          <div className="text-center p-4">
            <Spin size="large" />
            <p className="mt-3">{t('CHAT_JOB_DESCRIPTION_PROCESSING')}</p>
          </div>
        )}

        {extractedText && !processingPDF && (
          <div>
            <Text strong className="d-block mb-2">
              {t('CHAT_JOB_DESCRIPTION_PREVIEW')}:
            </Text>
            <div
              className="bg-light border rounded p-3 overflow-auto"
              style={{
                maxHeight: '300px',
                borderRadius: '6px',
              }}
            >
              <Paragraph
                className="mb-0"
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {extractedText.length > 1000
                  ? `${extractedText.substring(0, 1000)}...`
                  : extractedText}
              </Paragraph>
              {extractedText.length > 1000 && (
                <Text type="secondary" className="small">
                  {t('CHAT_JOB_DESCRIPTION_TEXT_TRUNCATED').replace('{{total}}', extractedText.length)}
                </Text>
              )}
            </div>
            <Text type="secondary" className="small mt-2 d-block">
              {t('CHAT_JOB_DESCRIPTION_CHAR_COUNT').replace('{{count}}', extractedText.length)}
            </Text>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default JobDescriptionModal;
